/**
 * The single RTK Query API slice.
 *
 * One slice, injected into from the files in ./endpoints. That is what makes
 * cache invalidation work across module boundaries: accepting a booking is a
 * rides mutation, but it changes the driver's earnings and the passenger's
 * notifications too, and a tag can only invalidate within one API slice.
 *
 * Tags name resources, not endpoints. `Ride` with an id invalidates that ride
 * everywhere it is cached; `Ride` as a bare `LIST` invalidates the collections.
 */

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQueryWithReauth from "./baseQuery";

/** Stands in for "the collection" in a tag id. */
export const LIST_ID = "LIST" as const;

export const api = createApi({
    reducerPath: "api",
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        "User",
        "Location",
        "Identity",
        "Ride",
        "RideStop",
        "Booking",
        "RideRequest",
        "DispatchOffer",
        "DriverOverview",
        "Earnings",
        "Car",
        "DriverApplication",
        "DriverDocument",
        "BankDetails",
        "DriverReadiness",
        "Notification",
        "NotificationPreferences",
        "PushDevice",
        "Rating",
        "RatingSummary",
        "Payment",
        "Conversation",
        "SupportConversation",
        // A safety report and its status. Separate from Feedback, which is the
        // landing page contact queue: this is the one where being slow causes
        // harm, and a reporter needs to watch the status move.
        "Incident",
        // A live share link for one ride. Tagged per ride, because creating,
        // reading and revoking all concern the same single share.
        "TripShare",
    ],
    // Sixty seconds of grace before an unsubscribed cache entry is dropped.
    // The default is 60 too; stating it makes the navigation behaviour explicit
    // rather than incidental: moving between dashboard tabs and back should
    // not refetch.
    keepUnusedDataFor: 60,
    // A tab left open overnight shows last night's rides until something
    // triggers a refetch. Refetching when the tab regains focus is the cheapest
    // correction available and costs nothing while the tab is hidden.
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: () => ({}),
});

export default api;
